/**
 * 冒険メニューカード
 * ホームの主要導線を統一したカード表現で表示します。
 */

import React from "react";
import { Pressable, Text, View } from "./WebPrimitives";
import AdventureMenuIcon, {
  type AdventureMenuIconName,
} from "./AdventureMenuIcon";
import type { GuidedTourAnchorProps } from "./guidedTourAnchor";

/** 冒険メニューカードのプロパティ */
type AdventureMenuCardProps = {
  accessibilityLabel: string;
  /** 使い方ガイドが指し示す目印（tourAnchor(...) の戻り値）。カードのボタンへ展開する */
  anchorProps?: GuidedTourAnchorProps;
  badge?: string;
  description: string;
  iconName: AdventureMenuIconName;
  isImportant?: boolean;
  onPress: () => void;
  testID?: string;
  title: string;
  tone?: "default" | "result";
};

/**
 * 冒険メニュー内の主要導線カードを表示する
 * @param {AdventureMenuCardProps} props - プロパティ
 * @returns {JSX.Element} 冒険メニューカード
 */
const AdventureMenuCard = ({
  accessibilityLabel,
  anchorProps,
  badge = "",
  description,
  iconName,
  isImportant = false,
  onPress,
  testID,
  title,
  tone = "default",
}: AdventureMenuCardProps): React.JSX.Element => {
  /** カードの状態別クラス */
  const toneClassName =
    tone === "result"
      ? "adventure-menu-card--result"
      : "adventure-menu-card--default";

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      className={`adventure-menu-card ${toneClassName} min-h-[82px] flex-grow rounded-none border-4 bg-[#FFF8DD] p-2 shadow-[4px_4px_0_rgba(22,36,58,0.24)] ${
        isImportant ? "border-[#DC2626]" : "border-app-quest-night"
      }`}
      onPress={onPress}
      style={{ width: "47%" }}
      testID={testID}
      {...anchorProps}
    >
      <View className="adventure-menu-card__content min-h-[58px] flex-row items-center gap-2">
        <View className="adventure-menu-card__icon h-9 w-9 shrink-0 items-center justify-center rounded-none border-2 border-app-quest-night bg-app-accent-soft text-app-quest-night">
          <AdventureMenuIcon
            className="text-app-quest-night"
            name={iconName}
            size={22}
          />
        </View>
        <View className="min-w-0 flex-1">
          <View className="flex-row items-start justify-between gap-1">
            <Text
              className="min-w-0 flex-1 text-[13px] font-extrabold leading-4 text-app-quest-night"
              numberOfLines={2}
            >
              {title}
            </Text>
            {badge ? (
              <View
                className={`shrink-0 rounded-none px-1.5 py-0.5 ${
                  isImportant
                    ? "bg-[#DC2626] text-white"
                    : "bg-app-quest-night text-app-panel"
                }`}
              >
                <Text className="text-[8px] font-extrabold leading-3">
                  {badge}
                </Text>
              </View>
            ) : null}
          </View>
          <Text
            className={`mt-1 text-[10px] leading-3 ${
              isImportant ? "text-[#991B1B]" : "text-app-text"
            }`}
            numberOfLines={2}
          >
            {description}
          </Text>
        </View>
      </View>
    </Pressable>
  );
};

export default AdventureMenuCard;
